import { FC } from "react";

import { stringAvatar } from "@/features/procedures/lib/checklist";
import { ShiftMetaBadge } from "@/features/shift/ui/components/ShiftMetaBadge";
import {
	Avatar,
	Box,
	Card,
	CardContent,
	Chip,
	Stack,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";

const stripHtml = (s?: string) =>
	(s || "")
		.replace(/<[^>]*>/g, " ")
		.replace(/\s+/g, " ")
		.trim();

const truncate = (s: string, n = 120) =>
	s.length > n ? `${s.slice(0, n - 1)}…` : s;

type ShiftEventRow = {
	Id: string | number;
	DateRaw?: string;
	LoggedByText?: string;
	DescriptionPlain?: string;
	Description?: string;
	Tags?: string[];
	ShiftName?: string;
	ShiftCrewText?: string;
	DayNight?: { Name?: string; Color?: string };
	ShiftCrew?: { Color?: string };
	Groups?: unknown[];
	GroupText?: string;
};

type MobileShiftEventCardsProps = {
	rows: ShiftEventRow[];
	timezone: string;
	onRowClick: (row: ShiftEventRow) => void;
};

export const MobileShiftEventCards: FC<MobileShiftEventCardsProps> = ({
	rows,
	timezone,
	onRowClick,
}) => {
	const theme = useTheme();

	return (
		<Box
			sx={{
				flex: 1,
				overflowY: "auto",
				overflowX: "hidden",
				WebkitOverflowScrolling: "touch",
				px: 0.5,
				py: 1,
			}}
		>
			<Stack spacing={1.25}>
				{rows.map((row) => {
					return (
						<Card
							key={row.Id}
							variant="outlined"
							sx={{
								borderRadius: 1.5,
								borderColor: "divider",
								overflow: "hidden",
								cursor: "pointer",
								transition: "background-color 0.2s",
								"&:active": {
									bgcolor: alpha(theme.palette.primary.main, 0.08),
								},
							}}
							onClick={() => onRowClick(row)}
							elevation={0}
						>
							<CardContent
								sx={{
									py: 1.25,
									px: 1.5,
									"&:last-child": { pb: 1.25 },
								}}
							>
								<Stack spacing={0.75}>
									{/* Date + Author */}
									<Stack
										direction="row"
										alignItems="center"
										justifyContent="space-between"
										flexWrap="wrap"
										gap={0.5}
									>
										<Typography
											variant="caption"
											sx={{
												fontVariantNumeric: "tabular-nums",
												fontWeight: 600,
											}}
										>
											{row.DateRaw
												? dayjs
														.utc(row.DateRaw)
														.tz(timezone)
														.format("DD/MM/YY HH:mm:ss")
												: "—"}
										</Typography>
										<Stack direction="row" alignItems="center" spacing={0.75}>
											{row.LoggedByText && (
												<Tooltip title={row.LoggedByText}>
													<Avatar
														{...stringAvatar(row.LoggedByText)}
														sx={{ width: 24, height: 24, fontSize: "0.7rem" }}
													/>
												</Tooltip>
											)}
											<Typography
												variant="caption"
												color="text.secondary"
												noWrap
												sx={{ maxWidth: 100 }}
											>
												{row.LoggedByText || "—"}
											</Typography>
										</Stack>
									</Stack>

									{/* Description (truncated) */}
									<Typography
										variant="body2"
										sx={{
											lineHeight: 1.4,
											display: "-webkit-box",
											WebkitLineClamp: 3,
											WebkitBoxOrient: "vertical",
											overflow: "hidden",
											"& p": { margin: 0 },
										}}
									>
										{truncate(
											stripHtml(row.DescriptionPlain ?? row.Description ?? ""),
											180,
										) || "—"}
									</Typography>

									{/* Tags */}
									{Array.isArray(row?.Tags) && row.Tags.length > 0 && (
										<Stack direction="row" flexWrap="wrap" gap={0.5}>
											{row.Tags.slice(0, 4).map((tag, idx) => (
												<Chip
													key={idx}
													label={tag}
													size="small"
													variant="outlined"
													sx={{ height: 20, fontSize: "0.7rem" }}
												/>
											))}
											{row.Tags.length > 4 && (
												<Chip
													label={`+${row.Tags.length - 4}`}
													size="small"
													sx={{ height: 20, fontSize: "0.7rem" }}
												/>
											)}
										</Stack>
									)}

									{/* Shift + Crew badges */}
									<Stack
										direction="row"
										flexWrap="wrap"
										gap={0.5}
										alignItems="center"
									>
										{row.DayNight?.Name && (
											<ShiftMetaBadge
												label={row.DayNight.Name}
												color={row.DayNight.Color}
												compact
												maxWidth={90}
											/>
										)}
										{row.ShiftCrewText && (
											<ShiftMetaBadge
												label={row.ShiftCrewText}
												color={row.ShiftCrew?.Color}
												compact
												maxWidth={110}
											/>
										)}
										{row.GroupText && (
											<Typography
												variant="caption"
												color="text.secondary"
												noWrap
												sx={{ maxWidth: 100 }}
											>
												{row.GroupText}
											</Typography>
										)}
									</Stack>
								</Stack>
							</CardContent>
						</Card>
					);
				})}
			</Stack>
		</Box>
	);
};
